import React, { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { useAdminEnums } from '../../../../api/enums'
import { AdminReviewCardSection, EnumOption } from '../../../../api/types'
import { useCreateAdminReviewCardSection, useUpdateAdminReviewCardSection } from '../../../../api/review-card-sections'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'
import { buildSectionTypeOptions } from '../notice-section-types'

interface SectionValues {
  displayName: string
  displayDescription: string
  sectionType: string
  order: number
  immutable: boolean
}

// Explicit "None" option — a None section holds manually-added dynamic fields (no component factory).
// Its value matches the backend SectionType.None member; the label is UI-only.
const NONE_SECTION_TYPE: EnumOption = { value: 'None', label: 'None – dynamic fields' }

const sectionSchema = yup.object({
  displayName: yup.string().optional().default(''),
  displayDescription: yup.string().optional().default(''),
  sectionType: yup.string().optional().default(''),
  order: yup.number().typeError('Enter a valid number').min(0, 'Order must be 0 or more').required('Enter an order'),
  immutable: yup.boolean().default(false),
})

interface SectionDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  reviewCardReferenceID: string
  /** The section being edited, or null to create a new one. */
  section: AdminReviewCardSection | null
  /** All sections on the card — used to prevent duplicate order values. */
  existingSections: AdminReviewCardSection[]
  /** Default order for a new section (appended to the end). */
  nextOrder: number
  /** The form's document family ("Agreement" | "Notice") — narrows the offered Section Types for a
   * Notice, since it has no tenant-signing flow. Absent/"Agreement" offers the full catalog. */
  family?: string
  onSaved: () => void
}

/**
 * Add/Edit drawer for a review card section. A Section Type (from `/api/enums`) makes the section
 * factory-driven (no manual fields); leaving it blank makes it a `None` section that holds dynamic fields.
 */
export const SectionDrawer: React.FC<SectionDrawerProps> = ({
  isOpen,
  onOpenChange,
  reviewCardReferenceID,
  section,
  existingSections,
  nextOrder,
  family,
  onSaved,
}) => {
  const formId = 'section-form'
  const { data: enums } = useAdminEnums()
  const [createSection, creating] = useCreateAdminReviewCardSection(reviewCardReferenceID)
  const [updateSection, updating] = useUpdateAdminReviewCardSection(reviewCardReferenceID, section?.referenceID ?? '')
  const isBusy = creating || updating

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm<SectionValues>({
    resolver: yupResolver(sectionSchema),
    defaultValues: { displayName: '', displayDescription: '', sectionType: 'None', order: nextOrder, immutable: false },
  })

  // For a Notice, only offer the SectionTypes that apply to it (see notice-section-types.ts) — everything
  // else is an agreement-only concept (payment methods, special conditions, repairers, tenant signatures).
  const sectionTypeOptions = useMemo<EnumOption[]>(
    () => buildSectionTypeOptions(enums?.SectionType ?? [], family, section?.sectionType, NONE_SECTION_TYPE),
    [enums, family, section],
  )
  const [typeOption, setTypeOption] = useState<EnumOption>(NONE_SECTION_TYPE)

  useEffect(() => {
    if (!isOpen) return
    reset({
      displayName: section?.displayName ?? '',
      displayDescription: section?.displayDescription ?? '',
      sectionType: section?.sectionType || 'None',
      order: section?.order ?? nextOrder,
      immutable: section?.immutable ?? false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, section])

  useEffect(() => {
    if (!isOpen) return
    const value = section?.sectionType || 'None'
    setTypeOption(sectionTypeOptions.find((option) => option.value === value) ?? NONE_SECTION_TYPE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, section, sectionTypeOptions])

  const handleClose = () => onOpenChange(false)

  const onSubmit: SubmitHandler<SectionValues> = async (values) => {
    const takenOrders = existingSections
      .filter((existing) => existing.referenceID !== section?.referenceID)
      .map((existing) => existing.order)
    if (takenOrders.includes(values.order)) {
      setError('order', {
        type: 'manual',
        message: `A section already exists with order ${values.order}. Choose a different order.`,
      })
      return
    }

    // An unset Section Type means a plain "None" section that carries manually-added dynamic fields.
    const payload = { ...values, sectionType: values.sectionType || 'None' }

    try {
      if (section) await updateSection(payload)
      else await createSection(payload)
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer isOpen={isOpen} title={section ? 'Edit Section' : 'Add Section'} onClose={handleClose} footerItems={footer}>
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          <InputGroup
            label="Display Name"
            placeholder="Optional — leave blank for factory-driven sections"
            {...register('displayName')}
            errorMessage={errors.displayName?.message}
          />
          <InputGroup
            label="Display Description"
            placeholder="Optional description shown on the section"
            {...register('displayDescription')}
            errorMessage={errors.displayDescription?.message}
          />
          <div>
            <Select
              label="Section Type"
              options={sectionTypeOptions}
              value={typeOption}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                const next = option ?? NONE_SECTION_TYPE
                setTypeOption(next)
                setValue('sectionType', next.value)
              }}
              errorMessage={errors.sectionType?.message}
            />
            <FieldHelp>
              A section type is built by a dedicated component that generates its fields automatically (e.g. Property
              Address, Signatures, Payment Method). Choose “None – dynamic fields” to add your own fields to the section
              instead.
            </FieldHelp>
          </div>
          <InputGroup
            label="Order *"
            inputMode="numeric"
            {...register('order', { valueAsNumber: true })}
            errorMessage={errors.order?.message}
          />
          <Checkbox
            label="Immutable"
            supplementaryInfo="When selected, agents can view this section but cannot edit its fields."
            checked={Boolean(watch('immutable'))}
            onChange={() => setValue('immutable', !watch('immutable'))}
          />
        </FieldStack>
      )}
    </Drawer>
  )
}
